import { Suspense, lazy, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigationType,
  useParams,
} from 'react-router';

import PublicUtilityBar from './components/PublicUtilityBar';
import RouteLoadingShell from './components/RouteLoadingShell';
import { AccountThemeSync, AuthSessionBar, useAccountTheme } from './features/auth';
import { businessLanguageFromPath } from './features/business/businessContent';
import {
  isLocaleAwarePublicPath,
  isPublicPracticeArtistPath,
  isPublicPracticeCatalogPath,
  isPublicPracticeRoomPath,
  isPublicPracticeSongPath,
  isPublicKnowledgeLanguage,
  isSupportedLanguage,
  pickLocalized,
  readLanguageFromPathname,
  useDocumentTitleFromPath,
  useLanguage,
} from './features/i18n';
import LandingRoute from './routes/LandingRoute';
import PublicPracticeArtistRoute from './routes/PublicPracticeArtistRoute';
import PublicPracticeCatalogRoute from './routes/PublicPracticeCatalogRoute';
import PublicPracticeSongRoute from './routes/PublicPracticeSongRoute';

const LoginRoute = lazy(() => import('./routes/LoginRoute'));
const ImpersonationRoute = lazy(() => import('./routes/ImpersonationRoute'));
const DemoStudioRoute = lazy(() => import('./routes/DemoStudioRoute'));
const SharedStudioRoute = lazy(() => import('./routes/SharedStudioRoute'));
const PublicKnowledgeRoute = lazy(() => import('./routes/PublicKnowledgeRoute'));
const PublicPracticeRoomRoute = lazy(() => import('./routes/PublicPracticeRoomRoute'));
const BlogIndexRoute = lazy(() => import('./routes/BlogIndexRoute'));
const BlogArticleRoute = lazy(() => import('./routes/BlogArticleRoute'));
const BusinessRoute = lazy(() => import('./routes/BusinessRoute'));
const StudioRoute = lazy(() => import('./routes/StudioRoute'));
const GuitarPlayRoute = lazy(() => import('./routes/GuitarPlayRoute'));
const AdminRoute = lazy(() => import('./routes/AdminRoute'));

function NotFoundRoute() {
  const { language } = useLanguage();
  const notFoundText = pickLocalized(language, {
    ko: '페이지를 찾을 수 없습니다.',
    en: 'Page not found.',
    ja: 'ページが見つかりません。',
  });

  return <div className="route-shell">404 — {notFoundText}</div>;
}

function LanguageRouteSync() {
  const location = useLocation();
  const { language, setLanguageFromRoute } = useLanguage();

  useEffect(() => {
    const normalizedPath = location.pathname === '/'
      ? location.pathname
      : location.pathname.replace(/\/+$/, '');
    const routeLanguage = normalizedPath === '/demo'
      ? 'ko'
      : businessLanguageFromPath(normalizedPath) ?? readLanguageFromPathname(normalizedPath);
    // URL prefix 가 있을 때만 라우트 기반 동기화. prefix 없는 라우트는
    // 직전 언어를 그대로 두어 sticky 회귀가 발생하지 않도록 한다 — 초기 로드 시
    // resolveDefaultLanguage 가 (exact root ko → prefix → storage → navigator → ko) 로 결정.
    // 공개 데모와 business 페이지는 canonical path가 카피 언어를 결정한다.
    if (routeLanguage && routeLanguage !== language) {
      setLanguageFromRoute(routeLanguage);
    }
  }, [language, location.pathname, setLanguageFromRoute]);

  return null;
}

function DocumentTitleSync() {
  const location = useLocation();
  return (
    isPublicPracticeSongPath(location.pathname)
    || isPublicPracticeCatalogPath(location.pathname)
    || isPublicPracticeArtistPath(location.pathname)
    || isPublicPracticeRoomPath(location.pathname)
  )
    ? null
    : <DefaultDocumentTitleSync pathname={location.pathname} />;
}

export function RouteScrollPolicy() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const previousPathname = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (
      previousPathname.current !== null
      && previousPathname.current !== pathname
      && navigationType !== 'POP'
    ) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }

    previousPathname.current = pathname;
  }, [navigationType, pathname]);

  return null;
}

function DefaultDocumentTitleSync({ pathname }: { pathname: string }) {
  const theme = useAccountTheme();
  useDocumentTitleFromPath(pathname, theme === 'asome-guitar' ? '어썸기타' : undefined);
  return null;
}

function isLandingRoute(pathname: string): boolean {
  const normalizedPath = pathname === '/'
    ? pathname
    : pathname.replace(/\/+$/, '');
  if (normalizedPath === '/') {
    return true;
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  return segments.length === 1 && isSupportedLanguage(segments[0]);
}

function LocalizedPublicRoute({
  children,
  demoOnly = false,
  publicKnowledgeOnly = false,
}: {
  children: ReactNode;
  demoOnly?: boolean;
  publicKnowledgeOnly?: boolean;
}) {
  const { locale } = useParams<{ locale: string }>();
  const isValidLocale = demoOnly
    ? locale === 'en'
    : publicKnowledgeOnly
      ? isPublicKnowledgeLanguage(locale)
      : isSupportedLanguage(locale);
  return isValidLocale ? <>{children}</> : <NotFoundRoute />;
}

export default function App() {
  const location = useLocation();
  const showAuthSessionBar =
    location.pathname === '/studio' ||
    location.pathname.startsWith('/studio/') ||
    location.pathname === '/admin' ||
    location.pathname.startsWith('/admin/') ||
    location.pathname === '/guitar-play' ||
    location.pathname.startsWith('/guitar-play/');
  const showPublicUtilityBar =
    isLocaleAwarePublicPath(location.pathname)
    && !isLandingRoute(location.pathname);

  return (
    <>
      <AccountThemeSync />
      <LanguageRouteSync />
      <DocumentTitleSync />
      <AuthSessionBar visible={showAuthSessionBar} />
      {showPublicUtilityBar ? <PublicUtilityBar /> : null}
      <Suspense fallback={<RouteLoadingShell route="app" />}>
        <RouteScrollPolicy />
        <Routes>
          <Route path="/" element={<LandingRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/impersonate" element={<ImpersonationRoute />} />
          <Route path="/demo" element={<DemoStudioRoute />} />
          <Route path="/business" element={<BusinessRoute />} />
          <Route path="/en/business" element={<BusinessRoute />} />
          <Route
            path="/:locale/demo"
            element={(
              <LocalizedPublicRoute demoOnly>
                <DemoStudioRoute />
              </LocalizedPublicRoute>
            )}
          />
          <Route path="/share/:token" element={<SharedStudioRoute />} />
          <Route
            path="/:locale/use-cases"
            element={(
              <LocalizedPublicRoute publicKnowledgeOnly>
                <PublicKnowledgeRoute />
              </LocalizedPublicRoute>
            )}
          />
          <Route
            path="/:locale/use-cases/:slug"
            element={(
              <LocalizedPublicRoute publicKnowledgeOnly>
                <PublicKnowledgeRoute />
              </LocalizedPublicRoute>
            )}
          />
          <Route
            path="/:locale/guides/:slug"
            element={(
              <LocalizedPublicRoute publicKnowledgeOnly>
                <PublicKnowledgeRoute />
              </LocalizedPublicRoute>
            )}
          />
          <Route
            path="/:locale/compare/:slug"
            element={(
              <LocalizedPublicRoute publicKnowledgeOnly>
                <PublicKnowledgeRoute />
              </LocalizedPublicRoute>
            )}
          />
          <Route
            path="/:locale/blog"
            element={(
              <LocalizedPublicRoute>
                <BlogIndexRoute />
              </LocalizedPublicRoute>
            )}
          />
          <Route
            path="/:locale/blog/:slug"
            element={(
              <LocalizedPublicRoute>
                <BlogArticleRoute />
              </LocalizedPublicRoute>
            )}
          />
          <Route
            path="/:locale/practice-songs"
            element={<PublicPracticeCatalogRoute />}
          />
          <Route
            path="/:locale/artists/:artistSlug"
            element={<PublicPracticeArtistRoute />}
          />
          <Route
            path="/:locale/practice-songs/:slug"
            element={<PublicPracticeSongRoute />}
          />
          <Route
            path="/:locale/practice/:slug"
            element={<PublicPracticeRoomRoute />}
          />
          <Route
            path="/:locale"
            element={(
              <LocalizedPublicRoute>
                <LandingRoute />
              </LocalizedPublicRoute>
            )}
          />
          <Route path="/studio/*" element={<StudioRoute />} />
          <Route path="/guitar-play/*" element={<GuitarPlayRoute />} />
          <Route path="/admin/*" element={<AdminRoute />} />
          <Route path="*" element={<NotFoundRoute />} />
        </Routes>
      </Suspense>
    </>
  );
}
